'use client';

import { cn } from '@commander/utils';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden', {
  variants: {
    // Every avatar is bordered unless a caller opts out, as the Quotes list's
    // initials avatar does to match its design.
    bordered: {
      true: 'border border-border/50',
      false: '',
    },
    size: {
      xxs: 'h-[22px] w-[22px] text-[10px]',
      xs: 'h-6 w-6 text-[10px]',
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
      xl: 'h-14 w-14 text-lg',
      '2xl': 'h-16 w-16 text-xl',
    },
    shape: {
      circle: 'rounded-full',
      square: 'rounded-md',
    },
  },
  defaultVariants: {
    bordered: true,
    size: 'md',
    shape: 'circle',
  },
});

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size, shape, bordered, ...props }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      data-slot="avatar"
      className={cn(avatarVariants({ size, shape, bordered }), className)}
      {...props}
    />
  )
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    data-slot="avatar-image"
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    data-slot="avatar-fallback"
    className={cn('flex h-full w-full items-center justify-center bg-muted font-medium', className)}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// Avatar Group Component
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  children?: React.ReactNode;
}

const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max = 3, size = 'md', children, ...props }, ref) => {
    const childrenArray = React.Children.toArray(children);
    const displayedChildren = max ? childrenArray.slice(0, max) : childrenArray;
    const remainingCount = max && childrenArray.length > max ? childrenArray.length - max : 0;

    const overlapClasses = {
      xs: '-space-x-2',
      sm: '-space-x-2.5',
      md: '-space-x-3',
      lg: '-space-x-3.5',
      xl: '-space-x-4',
      '2xl': '-space-x-5',
    };

    return (
      <div
        ref={ref}
        className={cn('flex items-center', overlapClasses[size], className)}
        {...props}
      >
        {displayedChildren.map((child, index) => {
          const key = React.isValidElement(child) && child.key != null ? child.key : undefined;
          return (
            <div
              key={key ?? `avatar-${index}`}
              className="relative inline-block ring-2 ring-background rounded-full"
            >
              {React.isValidElement(child) &&
                React.cloneElement(child as React.ReactElement<any>, { size })}
            </div>
          );
        })}
        {remainingCount > 0 && (
          <div className="relative inline-block ring-2 ring-background rounded-full">
            <Avatar size={size}>
              <AvatarFallback className="bg-muted text-muted-foreground">
                +{remainingCount}
              </AvatarFallback>
            </Avatar>
          </div>
        )}
      </div>
    );
  }
);
AvatarGroup.displayName = 'AvatarGroup';

// Status indicator for avatars
export interface AvatarStatusProps extends React.HTMLAttributes<HTMLSpanElement> {
  status?: 'online' | 'offline' | 'busy' | 'away';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const statusVariants = {
  online: 'bg-success',
  offline: 'bg-muted',
  busy: 'bg-destructive',
  away: 'bg-warning',
};

const positionVariants = {
  'top-right': 'top-0 right-0',
  'top-left': 'top-0 left-0',
  'bottom-right': 'bottom-0 right-0',
  'bottom-left': 'bottom-0 left-0',
};

const AvatarStatus: React.FC<AvatarStatusProps> = ({
  status = 'online',
  position = 'bottom-right',
  className,
  ...props
}) => (
  <span
    className={cn(
      'absolute h-3 w-3 rounded-full border-2 border-background',
      statusVariants[status],
      positionVariants[position],
      className
    )}
    {...props}
  />
);

// User Avatar (always round)
export interface UserAvatarProps extends Omit<AvatarProps, 'shape'> {
  name?: string;
  src?: string;
  status?: 'online' | 'offline' | 'busy' | 'away';
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  src,
  status,
  size = 'md',
  className,
  ...props
}) => {
  const initials = name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <div className="relative inline-block">
      <Avatar size={size} shape="circle" className={className} {...props}>
        <AvatarImage src={src} alt={name} />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      {status && <AvatarStatus status={status} />}
    </div>
  );
};

// Company Avatar (always square)
export interface CompanyAvatarProps extends Omit<AvatarProps, 'shape'> {
  name?: string;
  src?: string;
}

const CompanyAvatar: React.FC<CompanyAvatarProps> = ({
  name,
  src,
  size = 'md',
  className,
  ...props
}) => {
  const initials = name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'CO';

  return (
    <Avatar size={size} shape="square" className={className} {...props}>
      <AvatarImage src={src} alt={name} />
      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  AvatarStatus,
  UserAvatar,
  CompanyAvatar,
  avatarVariants,
};
